import * as React from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import { HasPermission } from '../../authorization/components/HasPermission';
import { AppPermissions } from '../../authorization/Permissions';
import { LoadingIndicator } from '../../../components/LoadingIndicator';
import type RulesImportResolutionStore from '../stores/RulesImportResolutionStore';
import type TestProjectDataStore from '../../test_projects/stores/TestProjectDataStore';
import RulesImportVisualStore from '../stores/RulesImportVisualStore';
import './RulesImportResolution.less';

type ResolutionContentProps = {
    store: RulesImportVisualStore;
    resolution: RulesImportResolutionStore;
};

type TestProjectOutcomeProps = {
    dataStore: TestProjectDataStore;
};

type TestProjectReviewProps = ResolutionContentProps & {
    dataStore: TestProjectDataStore;
};

const TestProjectOutcome = observer(({ dataStore }: TestProjectOutcomeProps) => {
    const outcome = dataStore.inputsChange;
    const impact = outcome?.impact;

    if (!outcome) {
        return null;
    }

    const retained = impact?.retained.reduce((count, input) => count + input.baselineCount, 0) || 0;
    const removed = impact?.removed.reduce((count, input) => count + input.baselineCount, 0) || 0;

    const getOutcomeMessage = () => {
        if (!impact) {
            return 'Test Project inputs updated.';
        }

        switch (outcome.outcome) {
            case 'mapping':
                if (removed > 0) {
                    return 'Inputs mapped. Unmapped baselines were deleted.';
                }

                return 'Inputs mapped. Existing baselines were kept.';
            case 'replacement':
                if (removed > 0) {
                    return 'Inputs updated. Previous tag baselines were deleted.';
                }

                return 'Input changed. There were no existing baselines to delete.';
            default:
                return 'Test Project inputs updated.';
        }
    };

    return (
        <Alert
            type="success"
            showIcon
            message={getOutcomeMessage()}
            description={
                impact && (
                    <>
                        <strong>
                            {retained} {retained === 1 ? 'baseline' : 'baselines'} retained
                        </strong>
                        ,{' '}
                        <strong>
                            {removed} {removed === 1 ? 'baseline' : 'baselines'} deleted
                        </strong>
                        .
                    </>
                )
            }
        />
    );
});

const TestProjectReview = observer(({ store, resolution, dataStore }: TestProjectReviewProps) => {
    const project = dataStore.testProject;

    const [tagsExpanded, setTagsExpanded] = React.useState(false);

    const onMapInputs = React.useCallback(() => resolution.openInputMapping(), [resolution]);

    if (!dataStore.canUseProject || !project) {
        return null;
    }

    const blockingTags = store
        .getReplacedTagsUsedInTestProjectsWithNames()
        .filter(usage => usage.testProjects.some(affected => affected.testProjectId === dataStore.testProjectId));

    return (
        <div className="rule-import-resolution-section">
            <div className="rule-import-project-facts">
                <div>
                    <Typography.Text>
                        {project.packageIds.length} {project.packageIds.length === 1 ? 'document' : 'documents'}
                    </Typography.Text>
                    <Typography.Text type="secondary">Test project scope</Typography.Text>
                </div>
                <div>
                    <Typography.Text>
                        {project.topics.length} {project.topics.length === 1 ? 'input' : 'inputs'} ·{' '}
                        {project.inputsType}
                    </Typography.Text>
                    <Typography.Paragraph
                        key={tagsExpanded ? 'expanded' : 'collapsed'}
                        type="secondary"
                        ellipsis={
                            tagsExpanded
                                ? false
                                : {
                                      rows: 2,
                                      expandable: true,
                                      symbol: 'Show tags',
                                      onExpand: () => setTagsExpanded(true)
                                  }
                        }
                    >
                        {blockingTags.map(usage => usage.tagName).join(', ')}
                    </Typography.Paragraph>
                    {tagsExpanded && (
                        <Button type="link" onClick={() => setTagsExpanded(false)}>
                            Hide tags
                        </Button>
                    )}
                </div>
                <div>
                    <Typography.Text>Existing baselines</Typography.Text>
                    <Typography.Text type="secondary">Preserve by mapping the input</Typography.Text>
                </div>
            </div>

            <HasPermission permissionClaim={AppPermissions.CanManageTestProjects} entityId={project.projectId}>
                <Alert
                    type="warning"
                    showIcon
                    message="These tags prevent the import. Map this project's inputs to IOTA App Bindings."
                />
                <Space wrap>
                    {resolution.canResolveTestProjectWithBindings && (
                        <Button
                            className="dark"
                            type="primary"
                            size="large"
                            onClick={onMapInputs}
                            disabled={resolution.editorStore.isSubmitting}
                        >
                            Map to IOTA binding · keep baselines
                        </Button>
                    )}
                </Space>
                <Typography.Paragraph type="secondary">
                    Mapping connects the current tag input to a binding and keeps its baselines. Inputs left unmapped
                    and their baselines are deleted only after confirmation.
                </Typography.Paragraph>
            </HasPermission>
        </div>
    );
});

const ImportBlockers = observer(({ store, resolution }: ResolutionContentProps) => {
    const blockers = store.getReplacedTagsUsedInTestProjectsWithNames();

    return (
        <div className="rule-import-resolution-section">
            <Typography.Paragraph>
                This import would replace tags used by test projects. The import has not changed any rules.
            </Typography.Paragraph>

            <div className="rule-import-affected-projects">
                {blockers.map(usage =>
                    usage.testProjects.map(affected => (
                        <div key={`${usage.tagId}-${affected.testProjectId}`} className="rule-import-affected-row">
                            <div>
                                <Typography.Text>{usage.tagName}</Typography.Text>
                                <Typography.Text type="secondary">
                                    Used in test project: {affected.testProjectName}
                                </Typography.Text>
                            </div>
                            <HasPermission
                                permissionClaim={AppPermissions.CanManageTestProjects}
                                entityId={store.pendingAttempt?.projectId}
                            >
                                <Button
                                    type="link"
                                    aria-label={`Open project ${affected.testProjectName}`}
                                    onClick={() => resolution.reviewTestProject(affected.testProjectId)}
                                >
                                    Open project
                                </Button>
                            </HasPermission>
                        </div>
                    ))
                )}
            </div>

            <HasPermission
                permissionClaim={AppPermissions.CanManageTestProjects}
                entityId={store.pendingAttempt?.projectId}
            >
                <Alert
                    type="warning"
                    showIcon
                    message={
                        <>
                            <strong>Keep your baselines:</strong> open each affected project and map its tag input to an
                            IOTA App Binding. The project links above take you to the next step.
                        </>
                    }
                />
                <Typography.Paragraph type="secondary">
                    Inputs left unmapped and their baselines are deleted only after confirmation. The import stays
                    blocked while any affected project still uses these tags.
                </Typography.Paragraph>
            </HasPermission>
        </div>
    );
});

export const RulesImportResolutionContent = observer(({ store, resolution }: ResolutionContentProps) => {
    const attempt = store.pendingAttempt;

    if (!attempt) {
        return null;
    }

    const dataStore = resolution.dataStore;
    const status = resolution.resolutionStatus;
    const currentProjectStillBlocksImport = store.affectedTestProjects.some(
        project => project.testProjectId === dataStore?.testProjectId
    );
    const remainingProjects = store.affectedTestProjects.filter(
        project => project.testProjectId !== dataStore?.testProjectId
    );

    const statusErrorMessage = dataStore?.inputsChange
        ? 'Unable to refresh project and import status. Saved changes are preserved.'
        : 'Unable to load project and import status.';

    return (
        <div className="rule-import-resolution">
            {dataStore && <TestProjectOutcome dataStore={dataStore} />}

            {status === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    message={statusErrorMessage}
                    action={<Button onClick={resolution.retryResolutionStatus}>Retry status check</Button>}
                    style={{ marginBottom: 16 }}
                />
            )}

            {status === 'loading' && <LoadingIndicator />}

            {dataStore && currentProjectStillBlocksImport && (
                <TestProjectReview
                    key={dataStore.testProjectId}
                    store={store}
                    resolution={resolution}
                    dataStore={dataStore}
                />
            )}

            {!dataStore && status === 'ready' && store.affectedTestProjects.length > 0 && (
                <ImportBlockers store={store} resolution={resolution} />
            )}

            {resolution.isReadyToImport && !dataStore?.inputsChange && (
                <div className="rule-import-resolution-section">
                    <Alert
                        type="success"
                        showIcon
                        message="No Test Projects reference the affected tags. The import can now continue."
                    />
                    <Typography.Paragraph>
                        After the import, run the updated test projects to review their results against the retained or
                        newly configured baselines.
                    </Typography.Paragraph>
                </div>
            )}

            {resolution.isReadyToImport && dataStore?.inputsChange && (
                <Typography.Paragraph>
                    All affected test projects have been updated. You can return to the rule package import.
                </Typography.Paragraph>
            )}

            {status === 'ready' && dataStore?.inputsChange && remainingProjects.length > 0 && (
                <div className="rule-import-resolution-section">
                    <Typography.Paragraph>
                        <Typography.Text strong>
                            {remainingProjects.length}{' '}
                            {remainingProjects.length === 1
                                ? 'affected test project remains.'
                                : 'affected test projects remain.'}
                        </Typography.Text>{' '}
                        Update them before retrying the rule package import.
                    </Typography.Paragraph>

                    {remainingProjects.map(affected => (
                        <div className="rule-import-affected-row" key={affected.testProjectId}>
                            <Typography.Text>{affected.testProjectName}</Typography.Text>
                            <HasPermission
                                permissionClaim={AppPermissions.CanManageTestProjects}
                                entityId={attempt.projectId}
                            >
                                <Button
                                    key={affected.testProjectId}
                                    type="link"
                                    aria-label={`Review project ${affected.testProjectName}`}
                                    onClick={() => resolution.reviewTestProject(affected.testProjectId)}
                                >
                                    Review
                                </Button>
                            </HasPermission>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export const RulesImportResolutionActions = observer(({ store, resolution }: ResolutionContentProps) => {
    const attempt = store.pendingAttempt;

    if (!attempt) {
        return null;
    }

    const dataStore = resolution.dataStore;
    const nextProject = store.affectedTestProjects.find(project => project.testProjectId !== dataStore?.testProjectId);
    const canReviewNextProject =
        resolution.resolutionStatus === 'ready' && !!nextProject && (!dataStore || !!dataStore.inputsChange);

    return (
        <>
            <Button
                className="light"
                size="large"
                onClick={resolution.cancelDialog}
                disabled={!resolution.canCancelDialog}
            >
                {resolution.isInitialWarning ? 'Close' : 'Back to import'}
            </Button>

            <HasPermission permissionClaim={AppPermissions.CanManageTestProjects} entityId={attempt.projectId}>
                {canReviewNextProject && nextProject && (
                    <Button
                        className="dark"
                        type="primary"
                        size="large"
                        onClick={() => resolution.reviewTestProject(nextProject.testProjectId)}
                    >
                        {dataStore ? 'Review next project' : 'Review affected projects'}
                    </Button>
                )}
            </HasPermission>
            {resolution.isReadyToImport && (
                <Button className="dark" type="primary" size="large" onClick={store.retryImport}>
                    Retry rule package import
                </Button>
            )}
        </>
    );
});
